
import * as Sentry from "@sentry/react";

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogPayload {
  message: string;
  context?: Record<string, any>;
  error?: Error;
  tags?: Record<string, string>;
}

// Environment check for development mode
const isDevelopment = process.env.NODE_ENV === 'development';

// Centralized logger with improved throttling
export const logger = {
  // Track recent logs to prevent spamming
  _recentLogs: new Map<string, { timestamp: number, count: number }>(),
  
  // Throttle check - don't log same message more than X times in Y ms
  _shouldThrottle(key: string, throttleMs = 5000): boolean {
    const now = Date.now();
    const recent = this._recentLogs.get(key);
    
    if (recent) {
      // If the same log appeared recently and too many times (more than 3)
      if (now - recent.timestamp < throttleMs) {
        if (recent.count > 3) {
          return true; // Throttle after 3 occurrences
        }
        
        // Increment counter but continue logging first few occurrences
        this._recentLogs.set(key, { 
          timestamp: recent.timestamp, // Keep original timestamp 
          count: recent.count + 1 
        });
        return false;
      }
      
      // Update the timestamp if enough time has passed
      this._recentLogs.set(key, { timestamp: now, count: 1 });
      return false;
    }
    
    // First time seeing this log
    this._recentLogs.set(key, { timestamp: now, count: 1 });
    return false;
  },
  
  // Generate a throttling key from a log payload
  _getThrottleKey(payload: string | LogPayload): string {
    if (typeof payload === 'string') {
      return payload;
    }
    // Create a consistent key based on message and limited context
    const contextKey = payload.context ? 
      Object.entries(payload.context)
        .filter(([key]) => !key.includes('data')) // Exclude large data fields
        .map(([k, v]) => `${k}:${typeof v === 'object' ? JSON.stringify(v).substring(0, 20) : v}`)
        .join('|')
      : '';
    
    return `${payload.message}:${contextKey.substring(0, 100)}`;
  },
  
  /**
   * Log debug information (development only)
   */
  debug: (payload: string | LogPayload) => {
    if (isDevelopment) {
      const data = normalizePayload(payload);
      const throttleKey = logger._getThrottleKey(payload);
      
      if (!logger._shouldThrottle(throttleKey)) {
        console.debug('[DEBUG]', data.message, data.context || '');
      }
    }
  },

  /**
   * Log informational messages
   */
  info: (payload: string | LogPayload) => {
    const data = normalizePayload(payload);
    const throttleKey = logger._getThrottleKey(payload);
    
    if (!logger._shouldThrottle(throttleKey)) {
      console.info('[INFO]', data.message, data.context || '');
      
      // Optionally capture breadcrumb in Sentry
      Sentry.addBreadcrumb({
        category: 'info',
        message: data.message,
        data: data.context,
        level: 'info',
      });
    }
  },

  /**
   * Log warning messages
   */
  warn: (payload: string | LogPayload) => {
    const data = normalizePayload(payload);
    const throttleKey = logger._getThrottleKey(payload);
    
    if (!logger._shouldThrottle(throttleKey)) {
      console.warn('[WARN]', data.message, data.context || '');
      
      // Send warning to Sentry as breadcrumb
      Sentry.addBreadcrumb({
        category: 'warning',
        message: data.message,
        data: data.context,
        level: 'warning',
      });
    }
  },

  /**
   * Log error messages (automatically sent to Sentry)
   */
  error: (payload: string | LogPayload) => {
    const data = normalizePayload(payload);
    const throttleKey = logger._getThrottleKey(payload);
    
    if (!logger._shouldThrottle(throttleKey)) {
      console.error('[ERROR]', data.message, data.context || '');
      
      // Always send errors to Sentry
      if (data.error) {
        // If we have an actual Error object
        Sentry.withScope((scope) => {
          if (data.context) scope.setContext('additional', data.context);
          if (data.tags) Object.entries(data.tags).forEach(([key, value]) => scope.setTag(key, value));
          Sentry.captureException(data.error);
        });
      } else {
        // If we only have a message
        Sentry.withScope((scope) => {
          if (data.context) scope.setContext('additional', data.context);
          if (data.tags) Object.entries(data.tags).forEach(([key, value]) => scope.setTag(key, value));
          Sentry.captureMessage(data.message, 'error');
        });
      }
    }
  },
  
  /**
   * Start a transaction for performance monitoring
   */
  startTransaction: (name: string, operation: string) => {
    return Sentry.startTransaction({
      name,
      op: operation
    });
  }
};

// Helper to normalize string or object payloads
function normalizePayload(payload: string | LogPayload): LogPayload {
  if (typeof payload === 'string') {
    return { message: payload };
  }
  return payload;
}
